import React, { useState } from 'react';
import Footer from "../components/Footer";

import { useTranslation } from 'react-i18next';
import SEOHead from "../components/SEOHead";
function AbTestingDemo() {
    const { t } = useTranslation();
const [activeVariant, setActiveVariant] = useState('A');

  const tests = [
    {
      id: 1,
      name: 'Homepage Hero Section',
      status: 'Active',
      variantA: { label: 'Original Hero', conversions: 4.2, visitors: 1240 },
      variantB: { label: 'Hero with Video', conversions: 6.8, visitors: 1180 },
    },
    {
      id: 2,
      name: 'CTA Button Color',
      status: 'Active',
      variantA: { label: 'Blue Button', conversions: 3.1, visitors: 980 },
      variantB: { label: 'Green Button', conversions: 3.9, visitors: 1020 },
    },
    {
      id: 3,
      name: 'Pricing Page Layout',
      status: 'Completed',
      variantA: { label: 'Standard Layout', conversions: 5.4, visitors: 2100 },
      variantB: { label: 'Featured Tier Highlight', conversions: 8.1, visitors: 2050 },
    },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <SEOHead
        title="A/B Testing Demo | Max OS Cloud"
        description="Try Max OS Cloud's A/B testing engine. Create variants, split traffic, measure conversions, and pick winners with statistical significance. Interactive demo."
        url="/demo/ab-testing"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Demos", item: "https://maxemployee.com/demo" },
            { "@type": "ListItem", position: 3, name: "A/B Testing", item: "https://maxemployee.com/demo/ab-testing" }
            ]
          }
        ]}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-900/30 via-gray-900 to-gray-950 border-b border-amber-800/30">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="inline-block bg-amber-500/20 text-amber-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
            INTERACTIVE DEMO
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
            A/B Testing Demo
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl">
            See how Max OS Cloud's A/B Testing engine helps you optimize conversion rates
            by testing different variants and measuring real results.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* How it works */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">How A/B Testing Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '1', title: 'Create Variants', desc: 'Design two or more versions of your page element' },
              { step: '2', title: 'Split Traffic', desc: 'Automatically route visitors to each variant' },
              { step: '3', title: 'Measure Results', desc: 'Track conversions, clicks, and engagement' },
              { step: '4', title: 'Pick Winner', desc: 'Statistical significance tells you which variant wins' },
            ].map((item) => (
              <div key={item.step} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="w-8 h-8 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center font-bold text-sm mb-3">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Live test simulation */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">{t('ab_testing_demo.live_test_simulation')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tests.map((test) => (
              <div key={test.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="p-5 border-b border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{test.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      test.status === 'Active'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {test.status}
                    </span>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  {/* Variant A */}
                  <div className={`p-3 rounded-lg border-2 transition-all ${
                    activeVariant === 'A' ? 'border-amber-500 bg-amber-500/5' : 'border-gray-700'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-amber-400">{t('ab_testing_demo.variant_a')}</span>
                      <span className="text-xs text-gray-500">{test.variantA.visitors} visitors</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{test.variantA.label}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-800 rounded-full h-2">
                        <div
                          className="bg-amber-500 h-2 rounded-full transition-all"
                          style={{ width: `${test.variantA.conversions * 10}%` }}
                        />
                      </div>
                      <span className="text-sm font-mono text-amber-400">{test.variantA.conversions}%</span>
                    </div>
                  </div>
                  {/* Variant B */}
                  <div className={`p-3 rounded-lg border-2 transition-all ${
                    activeVariant === 'B' ? 'border-green-500 bg-green-500/5' : 'border-gray-700'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-green-400">{t('ab_testing_demo.variant_b')}</span>
                      <span className="text-xs text-gray-500">{test.variantB.visitors} visitors</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{test.variantB.label}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-800 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${test.variantB.conversions * 10}%` }}
                        />
                      </div>
                      <span className="text-sm font-mono text-green-400">{test.variantB.conversions}%</span>
                    </div>
                  </div>
                  {/* Winner badge */}
                  {test.variantB.conversions > test.variantA.conversions && (
                    <div className="text-center text-xs text-green-400 bg-green-500/10 rounded-lg py-2">
                      Variant B wins by +{(test.variantB.conversions - test.variantA.conversions).toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">MaxEmployee A/B Testing Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: '🎯', title: 'Visual Test Builder', desc: 'Create A/B tests without coding using our drag-and-drop editor' },
              { icon: '📊', title: 'Real-time Analytics', desc: 'Monitor test performance with live dashboards and conversion tracking' },
              { icon: '🧮', title: 'Statistical Significance', desc: 'Built-in Bayesian analysis ensures results are reliable' },
              { icon: '🔄', title: 'Auto-optimization', desc: 'Automatically shift traffic to winning variants in real-time' },
              { icon: '🔗', title: 'Multi-channel Tests', desc: 'Run simultaneous tests across web, email, and chat' },
              { icon: '📋', title: 'Test History', desc: 'Full audit trail of all tests, results, and decisions' },
            ].map((feature) => (
              <div key={feature.title} className="flex gap-4 bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="text-2xl">{feature.icon}</div>
                <div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-amber-900/20 to-orange-900/20 border border-amber-800/30 rounded-2xl p-10">
          <h2 className="text-2xl font-bold mb-3">{t('ab_testing_demo.ready_to_optimize_your_conversions')}</h2>
          <p className="text-gray-400 mb-6">Start running A/B tests in minutes with Max OS Cloud.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/pricing" className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 px-8 rounded-lg transition">
              Start Free Trial
            </a>
            <a href="/docs/ab-testing" className="border border-gray-700 hover:border-gray-500 text-gray-300 font-medium py-3 px-8 rounded-lg transition">
              View Documentation
            </a>
          </div>
        </div>
      </div>
      <Footer />

    </div>
  );
}

export default AbTestingDemo;
